import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { Plus, Eye, UserPlus } from 'lucide-react';
import { listSchools } from '../../modules/platform/api/devteam';
import { School } from '../../modules/platform/types';
import { showErrorToast } from '../../utils/errorHandler';
import { ROUTES } from '../../utils/constants';
import { formatDate } from '../../utils/format';
import { getSchoolOnboardingListLabel } from '../../utils/onboardingDisplay';
import { Modal } from '../../components/ui/Modal';
import CreateSchoolForm from './components/CreateSchoolForm';
import { useSearchParams } from 'react-router-dom';
import InviteAdminModal from './components/InviteAdminModal';

export const SchoolsList: React.FC = () => {
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [selectedSchoolId, setSelectedSchoolId] = useState<string | null>(null);
  const [selectedSchoolName, setSelectedSchoolName] = useState<string>('');
  const [selectedSchoolContactEmail, setSelectedSchoolContactEmail] = useState<string>('');

  useEffect(() => {
    const loadSchools = async () => {
      setLoading(true);
      try {
        const data = await listSchools();
        setSchools(data);
      } catch (error) {
        showErrorToast(error);
      } finally {
        setLoading(false);
      }
    };

    loadSchools();
  }, []);

  useEffect(() => {
    if (searchParams.get('create') === '1') {
      setIsCreateOpen(true);
    }
  }, [searchParams]);

  const filteredSchools = schools.filter((school) => {
    const normalizedSearch = searchTerm.toLowerCase();
    return (
      school.name.toLowerCase().includes(normalizedSearch) ||
      school.id.toLowerCase().includes(normalizedSearch) ||
      (school.timezone || '').toLowerCase().includes(normalizedSearch)
    );
  });

  return (
    <Layout>
      <PageHeader
        title="Schools"
        subtitle="Manage all schools in the platform"
        actions={
          <Button onClick={() => {
            // open modal and set query param
            setIsCreateOpen(true);
            navigate(`${ROUTES.DEV_SCHOOLS}?create=1`);
          }}>
            <Plus className="w-4 h-4 mr-2" />
            Create School
          </Button>
        }
      />

      <Card>
        <CardContent padding="md">
          <div className="mb-4">
            <Input
              placeholder="Search schools..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-md"
            />
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : filteredSchools.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No schools found. Create your first school to get started.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  {/* <TableHead>ID</TableHead> */}
                  <TableHead>Status</TableHead>
                  <TableHead>Onboarding</TableHead>
                  <TableHead>Timezone</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSchools.map((school) => (
                  <TableRow key={school.id}>
                    <TableCell className="font-medium">{school.name}</TableCell>
                    {/* <TableCell className="text-xs font-mono text-gray-500">{school.id}</TableCell> */}
                    <TableCell>
                      <Badge variant={school.status === 'active' ? 'success' : 'gray'}>
                        {school.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={school.onboarding_state?.completed ? 'success' : 'warning'}>
                        {getSchoolOnboardingListLabel(school)}
                      </Badge>
                    </TableCell>
                    <TableCell>{school.timezone || '-'}</TableCell>
                    <TableCell>{school.created_at ? formatDate(school.created_at) : '-'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/dev/schools/${school.id}`)}
                          aria-label="View"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>

                        {school.onboarding_state?.completed === false && school.onboarding_state?.step !== 'admin_accepted' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedSchoolId(school.id);
                              setSelectedSchoolName(school.name);
                              setSelectedSchoolContactEmail(school.contact?.email || school.contact_email || '');
                              setIsInviteOpen(true);
                            }}
                            className="ml-2"
                          aria-label="Invite admin"
                          title="Invite admin"
                          >
                            <UserPlus className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      <Modal
        isOpen={isCreateOpen}
        onClose={() => {
          setIsCreateOpen(false);
          navigate(ROUTES.DEV_SCHOOLS);
        }}
        title="Create School"
        size="lg"
      >
        <div className="overflow-y-auto min-h-0 pr-2">
          <CreateSchoolForm
            showFooter={false}
            onCancel={() => {
              setIsCreateOpen(false);
              navigate(ROUTES.DEV_SCHOOLS);
            }}
            onSuccess={async () => {
              setIsCreateOpen(false);
              navigate(ROUTES.DEV_SCHOOLS);
              // refresh list
              setLoading(true);
              try {
                const data = await listSchools();
                setSchools(data);
              } catch (err) {
                showErrorToast(err);
              } finally {
                setLoading(false);
              }
            }}
          />
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setIsCreateOpen(false);
              navigate(ROUTES.DEV_SCHOOLS);
            }}
          >
            Cancel
          </Button>
          <Button type="submit" form="create-school-form" variant="primary">
            Create
          </Button>
        </div>
      </Modal>
      {/* Invite Admin modal */}
      <InviteAdminModal
        isOpen={isInviteOpen}
        schoolId={selectedSchoolId}
        schoolName={selectedSchoolName || undefined}
        defaultEmail={selectedSchoolContactEmail}
        onClose={() => {
          setIsInviteOpen(false);
          setSelectedSchoolId(null);
          setSelectedSchoolName('');
          setSelectedSchoolContactEmail('');
        }}
        onSuccess={() => {
          // no refresh required, but could be added
        }}
      />
    </Layout>
  );
};

